// A project's tracking (PRD §4.5, P2d), on its editor: forecast at completion and estimate to complete
// (PP4); baseline / current plan / actual + forecast by period, by role or person, in hours or dollars
// (PP5); named re-baselines (PP1) and resnap (PP3).

import { useState } from "react";
import { Body1, Button, Caption1, Card, Field, Input, Select, Subtitle2, Tab, TabList } from "@fluentui/react-components";
import { addDays, mondayOf, today, type CostBasis } from "../../../core";
import { errorText, send, useApi } from "../api";
import { DateInput } from "../DateInput";
import { money, pct, stamp } from "../format";
import { ErrorBar, Kpi, Loading, useMe, Warnings } from "../ui";

interface Cell {
  hours: number;
  cost: number;
}
interface TrackingResponse {
  money: boolean;
  today: string;
  frozenBefore: string | null;
  baselines: { number: number; reason: string; takenAt: string; takenBy: string }[];
  baseline: number | null;
  versions: { number: number; reason: string; takenAt: string; takenBy: string }[];
  forecast: {
    toDate: Cell & { revenue?: number; expenses?: number };
    remaining: Cell & { revenue?: number; expenses?: number };
    atCompletion: Cell & { revenue?: number; expenses?: number; margin?: number; marginPct?: number | null };
    plan: Cell & { revenue?: number; margin?: number; marginPct?: number | null };
    baseline: (Cell & { margin?: number; marginPct?: number | null }) | null;
  };
  compare: { periods: string[]; rows: { key: string; label: string; cells: { baseline: Cell; plan: Cell; actualForecast: Cell }[]; totals: { baseline: Cell; plan: Cell; actualForecast: Cell } }[] };
}

const h = (n: number) => `${Math.round(n).toLocaleString("en-US")} h`;

export function ProjectTracking({ projectId, status, basis, onChanged }: { projectId: string; status: string; basis: CostBasis; onChanged: () => void }) {
  const me = useMe();
  const canEdit = me.can("plans.edit");
  const [zoom, setZoom] = useState<"week" | "month" | "quarter">("month");
  const [by, setBy] = useState<"total" | "role" | "person">("total");
  const [unit, setUnit] = useState<"hours" | "money">("hours");
  const [baselineNo, setBaselineNo] = useState<number | null>(null);
  const data = useApi<TrackingResponse>(`/projects/${encodeURIComponent(projectId)}/tracking?zoom=${zoom}&by=${by}&basis=${basis}${baselineNo ? `&baseline=${baselineNo}` : ""}`);
  const [dialog, setDialog] = useState<"baseline" | "resnap" | null>(null);
  const d = data.data;
  if (!d) return data.loading ? <Loading /> : <ErrorBar message={data.error} />;
  const f = d.forecast;
  const show = (c: Cell) => (unit === "money" && d.money ? money(c.cost) : h(c.hours));
  const booked = ["booked", "delivered"].includes(status);

  return (
    <Card className="panel wide">
      <div className="panel-head">
        <Subtitle2>Tracking</Subtitle2>
        <div className="toolbar">
          {d.baselines.length > 0 && (
            <Select size="small" value={String(baselineNo ?? d.baseline ?? "")} onChange={(_, x) => setBaselineNo(Number(x.value))} aria-label="Compare with baseline">
              {d.baselines.map((b) => (
                <option key={b.number} value={b.number}>
                  Baseline {b.number}: {b.reason}
                </option>
              ))}
            </Select>
          )}
          {canEdit && booked && (
            <Button size="small" onClick={() => setDialog("baseline")}>
              Take a baseline…
            </Button>
          )}
          {canEdit && (
            <Button size="small" onClick={() => setDialog("resnap")}>
              Resnap…
            </Button>
          )}
        </div>
      </div>
      {d.frozenBefore && <Caption1 className="block">Resnapped: weeks before {d.frozenBefore} are the hours reported, and frozen.</Caption1>}

      <div className="kpis" aria-label="Forecast at completion">
        <Kpi label="Done to date" value={d.money ? money(f.toDate.cost) : h(f.toDate.hours)} sub={d.money ? `${h(f.toDate.hours)} reported` : "reported"} />
        <Kpi label="Estimate to complete" value={d.money ? money(f.remaining.cost) : h(f.remaining.hours)} sub={d.money ? `${h(f.remaining.hours)} left in the plan` : "left in the plan"} />
        <Kpi
          label="Forecast at completion"
          value={d.money ? money(f.atCompletion.cost) : h(f.atCompletion.hours)}
          sub={d.money ? `against ${money(f.plan.cost)} in the plan${f.baseline ? `, ${money(f.baseline.cost)} in the baseline` : ""}` : `against ${h(f.plan.hours)} planned`}
        />
        {d.money && f.atCompletion.marginPct != null && (
          <Kpi
            label="Margin at completion"
            value={pct(f.atCompletion.marginPct)}
            tone={f.plan.marginPct != null ? (f.atCompletion.marginPct >= f.plan.marginPct - 1e-9 ? "good" : "bad") : undefined}
            sub={`${money(f.atCompletion.margin ?? 0)}; plan ${f.plan.marginPct == null ? "—" : pct(f.plan.marginPct)}${f.baseline?.marginPct != null ? `, baseline ${pct(f.baseline.marginPct)}` : ""}`}
          />
        )}
      </div>

      <div className="toolbar">
        <TabList size="small" selectedValue={zoom} onTabSelect={(_, x) => setZoom(x.value as "week" | "month" | "quarter")} aria-label="Tracking periods">
          <Tab value="week">Weeks</Tab>
          <Tab value="month">Months</Tab>
          <Tab value="quarter">Quarters</Tab>
        </TabList>
        <TabList size="small" selectedValue={by} onTabSelect={(_, x) => setBy(x.value as "total" | "role" | "person")} aria-label="Tracking group by">
          <Tab value="total">Total</Tab>
          <Tab value="role">By role</Tab>
          <Tab value="person">By person</Tab>
        </TabList>
        {d.money && (
          <TabList size="small" selectedValue={unit} onTabSelect={(_, x) => setUnit(x.value as "hours" | "money")} aria-label="Show">
            <Tab value="hours">Hours</Tab>
            <Tab value="money">$ cost</Tab>
          </TabList>
        )}
      </div>
      <div className="table-scroll">
        <table className="edit-table compact tracking">
          <thead>
            <tr>
              <th />
              <th />
              {d.compare.periods.map((p) => (
                <th key={p} className="num">
                  {p}
                </th>
              ))}
              <th className="num">Total</th>
            </tr>
          </thead>
          <tbody>
            {d.compare.rows.map((r) =>
              (["baseline", "plan", "actualForecast"] as const).map((which, i) => (
                <tr key={`${r.key}-${which}`} className={i === 0 ? "group-start" : undefined}>
                  <td>{i === 0 ? <strong>{r.label}</strong> : ""}</td>
                  <td>
                    <Caption1>{which === "baseline" ? `Baseline${d.baseline ? ` ${baselineNo ?? d.baseline}` : ""}` : which === "plan" ? "Current plan" : "Actual + forecast"}</Caption1>
                  </td>
                  {r.cells.map((c, j) => (
                    <td key={j} className={`num${which === "actualForecast" && d.compare.periods[j] <= d.today.slice(0, d.compare.periods[j].length) ? " actual-cell" : ""}`}>
                      {c[which].hours ? show(c[which]) : ""}
                    </td>
                  ))}
                  <td className="num">
                    <strong>{show(r.totals[which])}</strong>
                  </td>
                </tr>
              )),
            )}
          </tbody>
        </table>
      </div>
      <Caption1 className="muted">
        Actual + forecast is what was reported up to today{d.money ? ", at actual cost" : ""}, then the current plan. The baseline keeps the rates of the day it was
        taken. {!d.baselines.length && "This project has no baseline yet: booking takes Baseline 1."}
      </Caption1>
      {d.versions.length > 0 && (
        <details>
          <summary>
            <Caption1>Earlier versions ({d.versions.length})</Caption1>
          </summary>
          {d.versions.map((v) => (
            <Caption1 key={v.number} className="block">
              Version {v.number}: {v.reason} · {v.takenBy}, {stamp(v.takenAt)}
            </Caption1>
          ))}
        </details>
      )}
      {dialog === "baseline" && <BaselineForm projectId={projectId} onDone={() => (setDialog(null), data.reload(), onChanged())} onCancel={() => setDialog(null)} />}
      {dialog === "resnap" && <ResnapForm projectId={projectId} frozenBefore={d.frozenBefore} onDone={() => (setDialog(null), data.reload(), onChanged())} onCancel={() => setDialog(null)} />}
    </Card>
  );
}

function BaselineForm({ projectId, onDone, onCancel }: { projectId: string; onDone: () => void; onCancel: () => void }) {
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string>();
  async function save() {
    setError(undefined);
    try {
      await send("POST", `/projects/${encodeURIComponent(projectId)}/baselines`, { reason });
      onDone();
    } catch (err) {
      setError(errorText(err));
    }
  }
  return (
    <div className="inline-form">
      <Body1>A baseline is a fixed snapshot of the plan, its schedule and the rates of today, to measure against. Take one for a change order or a re-scope.</Body1>
      <Field label="Why" required>
        <Input value={reason} onChange={(_, x) => setReason(x.value)} placeholder="e.g. Change order 2: phase two added" />
      </Field>
      <ErrorBar message={error} />
      <div className="toolbar">
        <Button appearance="primary" disabled={!reason.trim()} onClick={() => void save()}>
          Take the baseline
        </Button>
        <Button onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  );
}

function ResnapForm({ projectId, frozenBefore, onDone, onCancel }: { projectId: string; frozenBefore: string | null; onDone: () => void; onCancel: () => void }) {
  const [asOf, setAsOf] = useState(mondayOf(today()));
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string>();
  async function save() {
    setError(undefined);
    try {
      await send("POST", `/projects/${encodeURIComponent(projectId)}/resnap`, { asOf: mondayOf(asOf), reason });
      onDone();
    } catch (err) {
      setError(errorText(err));
    }
  }
  return (
    <div className="inline-form">
      <Body1>
        A resnap makes a new working plan: every week before the date becomes the hours actually reported on the project, and freezes; weeks from the
        date keep the plan and stay editable. The plan as it is now is kept as an earlier version.
      </Body1>
      {frozenBefore && <Warnings intent="info" items={[`Already resnapped to ${frozenBefore}; pick a later Monday.`]} />}
      <div className="toolbar">
        <Field label="As of (a Monday)">
          <DateInput type="date" value={asOf} onChange={(_, x) => x.value && setAsOf(x.value)} />
        </Field>
        <Field label="Why" required>
          <Input value={reason} onChange={(_, x) => setReason(x.value)} placeholder="e.g. Quarterly re-plan" />
        </Field>
      </div>
      <Caption1 className="block muted">Weeks before {mondayOf(asOf)} (through {addDays(mondayOf(asOf), -1)}) become reported hours.</Caption1>
      <ErrorBar message={error} />
      <div className="toolbar">
        <Button appearance="primary" disabled={!reason.trim()} onClick={() => void save()}>
          Resnap
        </Button>
        <Button onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  );
}
